import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { QuickSearchResults } from '../../../shared/entities/QuickSearchResults';
import { environment } from '../../../../environments/environment';
import { QuickSearchCriteria } from '../../../shared/entities/QuickSearchCriteria';
import { PaymentTypes } from '../../../shared/entities/PaymentTypes';
import { InvoicePayment } from '../../../shared/entities/InvoicePayment';
import { InvoicePaymentStatus } from '../../../shared/entities/InvoicePaymentStatus';
import { InvoicePaymentWithExpectedDepositDate } from '../../../shared/entities/InvoicePaymentWithExpectedDepositDate';
import { InvoiceAdjustments } from '../../../shared/entities/InvoiceAdjustments';
import { InvoiceAdjustmentReasons } from '../../../shared/entities/InvoiceAdjustmentReasons';
import { AddRevenue } from '../../../shared/entities/AddRevenue';
import { AddRevenueTax } from '../../../shared/entities/AddRevenueTax';
import { ActiveTaxList } from '../../../shared/entities/ActiveTaxList';
import { RevenueCompletedAdjustments } from '../../../shared/entities/RevenueCompletedAdjustments';
import { AddInvoiceAdjustment } from '../../../shared/entities/AddInvoiceAdjustment';
import { AddInvoiceAdjustmentTax } from '../../../shared/entities/AddInvoiceAdjustmentTax';
import { AdjustTaxes } from '../../../shared/entities/AdjustTaxes';
import { InvoiceAdjustmentToGSearchResults } from '../../../shared/entities/InvoiceAdjustmentToGSearchResults';
import { PostMigrateAdjustment } from '../../../shared/entities/PostMigrateAdjustment';
import { TaxListMatchingInvoiceDate } from '../../../shared/entities/TaxListMatchingInvoiceDate';
import { QueryMessageService } from '../broadcast/MessageDispatchingService';
import { AddedRevenueDataForGSearchResults } from '../../../shared/entities/AddedRevenueDataForGSearchResults';
import { RevenuePostAdjustments } from '../../../shared/entities/RevenuePostAdjustments';
import { AdjustmentsCompletedGB } from '../../../shared/entities/AdjustmentsCompletedGB';
import { AdjustmentsCompletedMI } from '../../../shared/entities/AdjustmentsCompletedMI';
import { InvoiceAdjustment } from '../../../shared/entities/InvoiceAdjustment';
import { AddRevenueWithTaxes } from '../../../shared/entities/AddRevenueWithTaxes';
import { RevenueTypes } from '../../../shared/entities/RevenueTypes';
import { RevenueHistoricalPostAdjustments } from '../../../shared/entities/RevenueHistoricalPostAdjustments';
import { RevenuePostAdjustmentQuery } from '../../../shared/entities/RevenuePostAdjustmentQuery';
import { ProcessedInvoicePayments } from '../../../shared/entities/ProcessedInvoicePayments';
import { PaidInvoicePayments } from '../../../shared/entities/PaidInvoicePayments';
import { PaymentRefundApprovalQuery } from '../../../shared/entities/PaymentRefundApprovalQuery';
import { ApnUtilitiesService } from './apn-utilities.service';
import { IMiBillingLineResponse } from 'src/app/shared/interfaces';

const QUICKSEARCH_ZERO_BALANCE_INVOICES = 'Invoice/QuickSearchAllInvoices';
const QUICKSEARCH_NON_ZERO_BALANCE_INVOICES = 'Invoice/QuickSearchNonZeroAccountBalance';
const QUICKSEARCH_ONLY_ZERO_BALANCE_INVOICES = 'Invoice/QuickSearchOnlyZeroAccountBalance';
const GET_INVOICE_DETAIL_BY_INVOICE_NUMBER = 'Invoice/GetInvoiceDetailsByInvoiceNumber';
const GET_PAYMENT_TYPES_FOR_LOT_NUMBER = 'Invoice/GetPaymentTypesForLotNumber';
const ADD_NEW_INVOICE_PAYMENT = 'Invoice/AddNewInvoicePayment';
const ADD_NEW_INVOICE_PAYMENT_LIST = 'Invoice/AddNewInvoicePaymentList';
const GET_INVOICE_PAYMENT_LIST_BY_INVOICE_ID = 'Invoice/GetInvoicePayments';
const ADD_INVOICE_PAYMENT_WITH_EXPECTED_DEPOSIT_DATE = 'Invoice/AddNewInvoicePaymentWithExpectedDepositDate';
const GET_ADJUSTMENTS_FOR_INVOICE_NUMBER_GB = 'Invoice/GetInvoiceAdjustmentsByInvoiceNumber_GB';
const GET_ADJUSTMENTS_FOR_INVOICE_NUMBER_MI = 'Invoice/GetInvoiceAdjustmentsByInvoiceNumber_MI';
const GET_INVOICE_ADJUSTMENT_REASONS_BY_LOT_NUMBER = 'Invoice/GetInvoiceAdjustmentReasonsByLotNumber';
const ADD_REVENUE_ADJUSTMENT = 'Invoice/AddInvoiceRevenueData';
const ADD_REVENUE_ADJUSTMENT_TAX = 'Invoice/AddRevenueAdjustmentTax';
const GET_REVENUE_INVOICE_ADJUSTMENTS_BY_INVOICE_NUMBER = 'Invoice/GetRevenueAdjustmentsByInvoiceNumber';
const GET_REVENUE_ADJUSTMENT_REASONS_BY_LOT_NUMBER = 'Invoice/GetRevenueAdjustmentReasonsByLotNumber';
const ADD_INVOICE_ADJUSTMENT = 'Invoice/AddIUAdjustmentPosting';
const ADD_INVOICE_ADJUSTMENT_TAX = 'Invoice/AddIUAdjustmentPostingTax';
const ADD_ADJUST_TAXES = 'Invoice/AdjustTaxes';
const ADD_MIGRATE_ADJUSTMENT_POST_EDIT = 'Invoice/MigrateAdjustmentPostingEdit';
const ADD_INVOICE_ADJUST_TO_G_SEARCH_RESULTS = 'Invoice/AdjustInvoiceToGSearchResults';
const GET_TAX_LIST_THAT_MATCH_INVOICE_DATE = 'Invoice/GetTaxesThatMatchInvoiceDate';
const ADD_ADDED_REVENUE_TO_G_SEARCH_RESULTS = 'Invoice/AddRevenueToGSearchResults';
const ADD_REVENUE_POST_ADJUSTMENT = 'Invoice/RevenuePostAdjustment';
const GET_ADJUSTMENTS_GB_COMPLETED = 'Invoice/GetAdjustmentsGbCompleted';
const GET_ADJUSTMENTS_MI_COMPLETED = 'Invoice/GetAdjustmentsMiCompleted';
const GET_REVENUE_TYPES_FOR_LOT_NUMBER = 'Invoice/GetRevenueTypesForLotNumber';
const GET_REVENUE_POST_ADJUSTMENT_BY_INVOICE_NUMBER = 'Invoice/GetRevenuePostAdjustmentsByInvoiceNumber';
const GET_STATUS_FOR_INVOICE_PAYMENT_REFUND = 'Invoice/GetStatusForInvoicePaymentRefund';
const ADD_INVOICE_ADJUSTMENT_DATA = 'Invoice/AddInvoiceAdjustmentData';

const MAXIMUM_NUMBER_OF_PAYMENTS_PER_SUBMISSION: number = 25;

const httpOptions = {
  headers: new HttpHeaders({
    'Content-Type': 'application/json',
  }),
};

@Injectable()
export class InvoiceService {
  readonly restServerEndPoint: string;

  constructor(
    private http: HttpClient,
    private messageService: QueryMessageService,
    private apnUtilityService: ApnUtilitiesService,
  ) {
    this.restServerEndPoint = environment.RestServer;
  }

  //This tracks the maximum number of payments per submission.  Anything more than this number will
  //prevent the user from being able to add payments to the queue
  public GetMaximumNumberOfConcurrentPayments(): number {
    return MAXIMUM_NUMBER_OF_PAYMENTS_PER_SUBMISSION;
  }

  getQuickSearchPastDueBalanceInvoiceList(quickSearchCriteria: QuickSearchCriteria): Observable<QuickSearchResults[]> {
    let queryString = this.PrepQuerySearchCriteriaRequest(quickSearchCriteria);

    var endpoint = this.restServerEndPoint + 'Invoice/search/pastduebalance' + `?${queryString}`;

    return this.http.get<QuickSearchResults[]>(endpoint);
  }

  getRefundApprovalForInvoicePayment(paymentData: PaymentRefundApprovalQuery): Observable<boolean> {
    let queryString: string = `InvoiceNumber=${paymentData.invoiceNumber}&ReferenceNumber=${paymentData.referenceNumber}
        &RefundAmount=${paymentData.refundAmount}&PaymentType=${paymentData.paymentType}`;

    var endpoint = this.restServerEndPoint + GET_STATUS_FOR_INVOICE_PAYMENT_REFUND + `?${queryString}`;

    return this.http.get<boolean>(endpoint);
  }

  getRevenuePostAdjustmentsCompletedByInvoiceNumber(
    queryCriteria: RevenuePostAdjustmentQuery,
  ): Observable<RevenueHistoricalPostAdjustments[]> {
    let queryString: string = `invoiceNumber=${queryCriteria.invoiceNumber}&LotNumber=${queryCriteria.lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_REVENUE_POST_ADJUSTMENT_BY_INVOICE_NUMBER + `?${queryString}`;

    return this.http.get<RevenueHistoricalPostAdjustments[]>(endpoint);
  }

  getAdjustmentsCompleted_GB(invoiceNumber: string): Observable<AdjustmentsCompletedGB[]> {
    let queryString: string = `invoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_ADJUSTMENTS_GB_COMPLETED + `?${queryString}`;

    return this.http.get<AdjustmentsCompletedGB[]>(endpoint);
  }

  getAdjustmentsCompleted_MI(invoiceNumber: string): Observable<AdjustmentsCompletedMI[]> {
    let queryString: string = `invoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_ADJUSTMENTS_MI_COMPLETED + `?${queryString}`;

    return this.http.get<AdjustmentsCompletedMI[]>(endpoint);
  }

  getRevenueAdjustmentsReasonsForLotNumber(lotNumber: string): Observable<InvoiceAdjustmentReasons[]> {
    let queryString: string = `lotNumber=${lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_INVOICE_ADJUSTMENT_REASONS_BY_LOT_NUMBER + `?${queryString}`;

    return this.http.get<InvoiceAdjustmentReasons[]>(endpoint);
  }

  getTaxListThatMatchInvoiceDate(invoiceNumber: string): Observable<TaxListMatchingInvoiceDate[]> {
    let queryString: string = `invoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_TAX_LIST_THAT_MATCH_INVOICE_DATE + `?${queryString}`;

    return this.http.get<TaxListMatchingInvoiceDate[]>(endpoint);
  }

  getRevenueAdjustmentsForInvoiceNumber(invoiceNumber: string): Observable<RevenueCompletedAdjustments[]> {
    let queryString: string = `InvoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_REVENUE_INVOICE_ADJUSTMENTS_BY_INVOICE_NUMBER + `?${queryString}`;

    return this.http.get<RevenueCompletedAdjustments[]>(endpoint);
  }

  getPaymentListForInvoiceNumber(invoiceNumber: string): Observable<PaidInvoicePayments[]> {
    let queryString: string = `InvoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_INVOICE_PAYMENT_LIST_BY_INVOICE_ID + `?${queryString}`;

    return this.http.get<PaidInvoicePayments[]>(endpoint);
  }

  getPaymentTypesForLotNumber(lotNumber: string, sourceFunction: string): Observable<PaymentTypes[]> {
    let queryString: string = `LotNumber=${lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_PAYMENT_TYPES_FOR_LOT_NUMBER + `?${queryString}`;

    return this.http.get<PaymentTypes[]>(endpoint);
  }

  getRevenueTypesForLotNumber(lotNumber: string): Observable<RevenueTypes[]> {
    let queryString: string = `LotNumber=${lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_REVENUE_TYPES_FOR_LOT_NUMBER + `?${queryString}`;

    return this.http.get<RevenueTypes[]>(endpoint);
  }

  getAdjustmentReasonsForLotNumber(lotNumber: string): Observable<InvoiceAdjustmentReasons[]> {
    let queryString: string = `LotNumber=${lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_INVOICE_ADJUSTMENT_REASONS_BY_LOT_NUMBER + `?${queryString}`;

    return this.http.get<InvoiceAdjustmentReasons[]>(endpoint);
  }

  getAdjustmentsForInvoiceNumber_GB(invoiceNumber: string): Observable<InvoiceAdjustments[]> {
    let queryString: string = `InvoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_ADJUSTMENTS_FOR_INVOICE_NUMBER_GB + `?${queryString}`;

    return this.http.get<InvoiceAdjustments[]>(endpoint);
  }

  getAdjustmentsForInvoiceNumber_MI(invoiceNumber: string): Observable<InvoiceAdjustments[]> {
    let queryString: string = `InvoiceNumber=${invoiceNumber}`;

    var endpoint = this.restServerEndPoint + GET_ADJUSTMENTS_FOR_INVOICE_NUMBER_MI + `?${queryString}`;

    return this.http.get<InvoiceAdjustments[]>(endpoint);
  }

  getInvoiceDetailByInvoiceNumber(invoiceNumber: string, lotNumber: string): Observable<QuickSearchResults> {
    let queryString: string = `InvoiceNumber=${invoiceNumber}&LotNumber=${lotNumber}`;

    var endpoint = this.restServerEndPoint + GET_INVOICE_DETAIL_BY_INVOICE_NUMBER + `?${queryString}`;

    return this.http.get<QuickSearchResults>(endpoint);
  }

  getQuickSearchZeroBalanceInvoiceList(quickSearchCriteria: QuickSearchCriteria): Observable<QuickSearchResults[]> {
    let queryString = this.PrepQuerySearchCriteriaRequest(quickSearchCriteria);

    var endpoint = this.restServerEndPoint + QUICKSEARCH_ZERO_BALANCE_INVOICES + `?${queryString}`;

    return this.http.get<QuickSearchResults[]>(endpoint);
  }

  getQuickSearchNonZeroBalanceInvoiceList(quickSearchCriteria: QuickSearchCriteria): Observable<QuickSearchResults[]> {
    let queryString = this.PrepQuerySearchCriteriaRequest(quickSearchCriteria);

    var endpoint = this.restServerEndPoint + QUICKSEARCH_NON_ZERO_BALANCE_INVOICES + `?${queryString}`;

    return this.http.get<QuickSearchResults[]>(endpoint);
  }

  getQuickSearchOnlyZeroBalanceInvoiceList(quickSearchCriteria: QuickSearchCriteria): Observable<QuickSearchResults[]> {
    let queryString = this.PrepQuerySearchCriteriaRequest(quickSearchCriteria);

    var endpoint = this.restServerEndPoint + QUICKSEARCH_ONLY_ZERO_BALANCE_INVOICES + `?${queryString}`;

    return this.http.get<QuickSearchResults[]>(endpoint);
  }

  getQuickSearchOnlyCreditBalanceInvoiceList(
    quickSearchCriteria: QuickSearchCriteria,
  ): Observable<QuickSearchResults[]> {
    let queryString = this.PrepQuerySearchCriteriaRequest(quickSearchCriteria);

    var endpoint = this.restServerEndPoint + 'Invoice/QuickSearchCreditInvoices' + `?${queryString}`;

    return this.http.get<QuickSearchResults[]>(endpoint);
  }

  postInvoicePayment(invoicePayment: InvoicePayment): number {
    const headers = {};
    let rowsInserted: number = 0;

    var endpoint = this.restServerEndPoint + ADD_NEW_INVOICE_PAYMENT;

    this.http.post<any>(endpoint, invoicePayment, { headers }).subscribe((data) => {
      rowsInserted = data;
    });

    return rowsInserted;
  }

  postInvoicePayment_ex(invoicePayment: InvoicePayment[]): Observable<ProcessedInvoicePayments[]> {
    const headers = {};

    let rowsInserted: number = 0;

    var endpoint = this.restServerEndPoint + ADD_NEW_INVOICE_PAYMENT_LIST;

    return this.http.post<any>(endpoint, invoicePayment, { headers });
  }

  postInvoicePaymentWithExpectedDepositDate(
    invoicePayment: InvoicePaymentWithExpectedDepositDate,
  ): Observable<boolean> {
    const headers = {};

    var endpoint = this.restServerEndPoint + ADD_INVOICE_PAYMENT_WITH_EXPECTED_DEPOSIT_DATE;

    return this.http.post<any>(endpoint, invoicePayment, { headers });
  }

  postIUAdjustment(invoiceAdjustment: AddInvoiceAdjustment): Observable<number> {
    let adjustmentPostingId: number = 0;

    const headers = {};

    var endpoint = this.restServerEndPoint + ADD_INVOICE_ADJUSTMENT;

    return this.http.post<any>(endpoint, invoiceAdjustment, { headers });
  }

  postInvoiceAdjustmentAllData(invoiceData: InvoiceAdjustment): Observable<boolean> {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_INVOICE_ADJUSTMENT_DATA;

    return this.http.post<any>(endpoint, invoiceData, { headers });
  }

  postIUAdjustmentTax(invoiceAdjustmentTax: AddInvoiceAdjustmentTax): number {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_INVOICE_ADJUSTMENT_TAX;

    this.http.post<any>(endpoint, invoiceAdjustmentTax, { headers }).subscribe((data) => {
      rowsReturned = data;
    });

    return rowsReturned;
  }

  postAdjustTaxes(adjustTaxesData: AdjustTaxes, totalTaxes: number, adjustmentNetAmount: number): number {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_ADJUST_TAXES;

    this.http.post<any>(endpoint, adjustTaxesData, { headers }).subscribe((data) => {
      rowsReturned = data;
      this.postMigrateAdjustment(adjustTaxesData.invoiceNumber);

      let adjustToGSearchResults: InvoiceAdjustmentToGSearchResults = new InvoiceAdjustmentToGSearchResults();

      adjustToGSearchResults.invoiceNumber = adjustTaxesData.invoiceNumber;
      adjustToGSearchResults.AmountOfChange = totalTaxes + adjustmentNetAmount;

      this.postAdjustmentToGSearchResults(adjustToGSearchResults);
    });

    return rowsReturned;
  }

  postMigrateAdjustment(invoiceNumber: string): number {
    const headers = {};
    let rowsReturned: number = 0;

    let postMigrateAdjustment: PostMigrateAdjustment = new PostMigrateAdjustment();
    postMigrateAdjustment.invoiceNumber = invoiceNumber;

    var endpoint = this.restServerEndPoint + ADD_MIGRATE_ADJUSTMENT_POST_EDIT;

    this.http.post<any>(endpoint, postMigrateAdjustment, { headers }).subscribe((data) => {
      rowsReturned = data;
    });

    return rowsReturned;
  }

  postAdjustmentToGSearchResults(adjustToGSearchResultsData: InvoiceAdjustmentToGSearchResults): number {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_INVOICE_ADJUST_TO_G_SEARCH_RESULTS;

    this.http.post<any>(endpoint, adjustToGSearchResultsData, { headers }).subscribe(
      (data) => {
        rowsReturned = data;
      },
      (err: any) => {},
      () => {
        this.messageService.sendMessageToQueryInvoiceLine(adjustToGSearchResultsData.invoiceNumber);
      },
    );

    return rowsReturned;
  }

  postAddedRevenueAdjustment(addedRevenueData: AddRevenue, taxList: ActiveTaxList[]): Observable<number> {
    const headers = {};
    let revenueAdjustmentWithTax: AddRevenueWithTaxes = new AddRevenueWithTaxes();
    revenueAdjustmentWithTax.addRevenue = addedRevenueData;

    let addedRevenueTaxData: AddRevenueTax[] = [];
    let rowsReturned: number;
    let i: number = 0;
    let totalNetRevenue: number = addedRevenueData.quantity * addedRevenueData.billingRate;

    if (addedRevenueData.taxAmount > 0) {
      //this is taxable

      addedRevenueTaxData.push({
        revenueAdjustmentTaxId: 0,
        revenueAdjustmentId: 0,
        invoiceNumber: addedRevenueData.invoiceNumber,
        revenueType: addedRevenueData.revenueType,
        taxType: 'Total',
        taxAmount: addedRevenueData.taxAmount,
        taxAmountMoney: addedRevenueData.taxAmount,
      });
    }

    revenueAdjustmentWithTax.addRevenueTax = addedRevenueTaxData;

    var endpoint = this.restServerEndPoint + ADD_REVENUE_ADJUSTMENT;

    return this.http.post<any>(endpoint, revenueAdjustmentWithTax, { headers });
  }

  testDoTaxRevenue(revenueAdjustmentId: number): void {}

  postAddedRevenueAdjustmentTax(addedRevenueDataTax: AddRevenueTax): number {
    const headers = {};
    let recordModified: number = 0;

    try {
      var endpoint = this.restServerEndPoint + ADD_REVENUE_ADJUSTMENT_TAX;

      this.http.post<any>(endpoint, addedRevenueDataTax, { headers }).subscribe((data) => {
        recordModified = data;
      });
    } catch (err) {
      recordModified = -1;
    }

    return recordModified;
  }

  private PrepQuerySearchCriteriaRequest(queryValues: QuickSearchCriteria): string {
    let queryString: string = '';

    queryString = `LotNumber=${queryValues.lotNumber}`;

    //Company Name
    if (queryValues.companyName !== '') {
      queryValues.companyName = queryValues.companyName.replace('&', '%26');
      queryString += `&CompanyName=${queryValues.companyName}`;
    }

    //Company Name
    if (queryValues.invoiceType !== '' && queryValues.invoiceType !== 'ALL') {
      queryString += `&InvoiceType=${queryValues.invoiceType}`;
    }

    //Parker (CustomerName)
    if (queryValues.parkerName !== '') {
      queryValues.parkerName = queryValues.parkerName.replace('&', '%26');
      queryString += `&ParkerName=${queryValues.parkerName}`;
    }

    //Invoice Number
    if (queryValues.invoiceNumber !== '') {
      queryString += `&InvoiceNumber=${queryValues.invoiceNumber}`;
    }

    //Customer Type
    if (queryValues.customerType === 'ALL') queryString += `&CustomerType=${''}`;

    if (queryValues.customerType === 'PARKERPAID') queryString += `&CustomerType=${'P'}`;

    if (queryValues.customerType === 'COMPANYPAID') queryString += `&CustomerType=${'C'}`;

    //Revenue Start Date
    if (queryValues.revenueStartDate !== '') {
      queryString += `&RevenueStartDate=${queryValues.revenueStartDate}`;
    }

    //Revenue End Date
    if (queryValues.revenueEndDate !== '') {
      queryString += `&RevenueEndDate=${queryValues.revenueEndDate}`;
    }

    //Transaction Start Date
    if (queryValues.transactionStartDate !== '') {
      queryString += `&TransactionStartDate=${queryValues.transactionStartDate}`;
    }

    //Transaction End Date
    if (queryValues.transactionEndDate !== '') {
      queryString += `&TransactionEndDate=${queryValues.transactionEndDate}`;
    }

    if (queryValues.startDateForQuery !== '') {
      queryString += `&StartDateForQuery=${queryValues.startDateForQuery}`;
    }

    return queryString;
  }

  postAddedRevenueToGSearchResults(addedRevenueData: AddedRevenueDataForGSearchResults): Observable<number> {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_ADDED_REVENUE_TO_G_SEARCH_RESULTS;

    //return this.http.post<RevenueCompletedAdjustments[]>(endpoint);
    return this.http.post<any>(endpoint, addedRevenueData, { headers });
  }

  postAddRevenuePostAdjustment(revenuePostAdjustment: RevenuePostAdjustments): Observable<number> {
    const headers = {};
    let rowsReturned: number = 0;

    var endpoint = this.restServerEndPoint + ADD_REVENUE_POST_ADJUSTMENT;

    return this.http.post<any>(endpoint, revenuePostAdjustment, { headers });
  }
}
